import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomerService, Customer, ListCustomersResponse } from '../api/api_pb';
import { useClient } from '../connect_api';
import RelativeTime from '../components/RelativeTime';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../GlobalContext';
import { 
    Users, 
    Phone, 
    Calendar,
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Eye,
    User,
    Clock
} from 'lucide-react';

const Customers: React.FC = () => {
    const customerClient = useClient(CustomerService);
    const location = useLocation();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [totalCustomers, setTotalCustomers] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const itemsPerPage = 20;
    const { updateCustomers } = useGlobalContext();

    const query = new URLSearchParams(location.search);
    const pageParam = query.get('page');
    const page = pageParam ? Math.abs(parseInt(pageParam, 10)) : 1;

    // Debounce search term to reduce API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 400);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchCustomers = useCallback(async (page: number, searchQuery: string): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await customerClient.listCustomers({
                limit: itemsPerPage,
                page,
                mobileNumberQuery: searchQuery
            });
            const { customers: fetchedCustomers, total } = response as ListCustomersResponse;
            setTotalCustomers(total);
            setCustomers(fetchedCustomers);
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to fetch customers');
            console.error('Failed to fetch customers:', error);
        } finally {
            setIsLoading(false);
        }
    }, [customerClient]);

    const handleViewDetailsClick = (customer: Customer) => {
        updateCustomers(customer);
        navigate(`/customer/${customer.uuid}`);
    };

    // Reset to page 1 when search term changes
    useEffect(() => {
        if (debouncedSearchTerm !== '') {
            navigate('?page=1');
        }
    }, [debouncedSearchTerm, navigate]);

    // Fetch customers when page or search term changes
    useEffect(() => {
        fetchCustomers(page, debouncedSearchTerm);
    }, [page, debouncedSearchTerm, fetchCustomers]);

    const totalPages = Math.ceil(totalCustomers / itemsPerPage);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            navigate(`?page=${newPage}`);
        }
    };

    const CustomerCard = ({ customer }: { customer: Customer }) => (
        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                            <User className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{customer.mobileNumber}</h3>
                            <p className="text-sm text-gray-600">Customer</p>
                        </div>
                    </div>
                </div>

                {/* Info */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Mobile Number</span>
                        <span className="text-gray-900 font-medium ml-auto">{customer.mobileNumber}</span>
                    </div>
                    
                    {customer.createdAt && (
                        <div className="flex items-start gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-gray-600">Joined</span>
                                <p className="text-gray-900">
                                    <RelativeTime timestamp={customer.createdAt} />
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <button
                    onClick={() => handleViewDetailsClick(customer)}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                >
                    <Eye className="w-4 h-4" />
                    View Details
                </button>
            </div>
        </div>
    );

    const CustomerListItem = ({ customer }: { customer: Customer }) => (
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    <User className="w-5 h-5" />
                </div>
                
                {/* Main Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900">{customer.mobileNumber}</h3>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {customer.mobileNumber}
                        </span>
                        {customer.createdAt && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <RelativeTime timestamp={customer.createdAt} />
                            </span>
                        )}
                    </div>
                </div>

                {/* Action */}
                <button
                    onClick={() => handleViewDetailsClick(customer)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex-shrink-0"
                    title="View Details"
                >
                    <Eye className="w-5 h-5" />
                </button>
            </div>
        </div>
    );

    const PaginationControls = () => {
        const pageNumbers = [];
        const delta = 2;
        
        for (let i = 1; i <= totalPages; i++) {
            if (i <= delta || i > totalPages - delta || (i >= page - delta && i <= page + delta)) {
                pageNumbers.push(i);
            } else if (pageNumbers[pageNumbers.length - 1] !== '...') {
                pageNumbers.push('...');
            }
        }

        return (
            <div className="flex items-center justify-center gap-1 flex-wrap">
                <button
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                >
                    <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                
                {pageNumbers.map((number, index) =>
                    number === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-3 py-2">...</span>
                    ) : (
                        <button
                            key={`page-${number}`}
                            onClick={() => handlePageChange(number as number)}
                            className={`px-3 py-2 rounded-lg transition-colors ${
                                number === page 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            {number}
                        </button>
                    )
                )}
                
                <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                >
                    <ChevronsRight className="w-4 h-4" />
                </button>
            </div>
        );
    };

    return (
        <div className="p-6 animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Customers</h1>
                        <p className="text-gray-600">Manage your customer base</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="relative">
                            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search by mobile..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-modern pl-10 pr-4 py-2 w-full sm:w-64"
                            />
                        </div>
                        
                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'grid' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'list' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Total Customers</p>
                        <p className="text-3xl font-bold text-gray-900">{totalCustomers.toLocaleString()}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, totalCustomers)} of {totalCustomers}
                        </p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                        <Users className="w-8 h-8 text-white" />
                    </div>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading customers...</p>
                    </div>
                </div>
            ) : customers.length === 0 ? (
                <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Customers Found</h2>
                    <p className="text-gray-600">
                        {searchTerm ? 'No customers match your search criteria.' : 'No customers in the system yet.'}
                    </p>
                </div>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                            {customers.map((customer) => (
                                <CustomerCard key={customer.uuid} customer={customer} />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3 mb-8">
                            {customers.map((customer) => (
                                <CustomerListItem key={customer.uuid} customer={customer} />
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                            <PaginationControls />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Customers;